import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  Button,
  CircularProgress,
  Alert,
  AlertIcon,
  Divider,
  IconButton,
  Tag,
  Flex,
  Skeleton,
  useColorModeValue,
} from '@chakra-ui/react';
import { react_api } from '../../../../config';

const GoalInsightsView = ({ currentSummary, onGoalSelect, colors, username, bearerToken }) => {
  // Local state for this component
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goals, setGoals] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const [hasGeneratedInsights, setHasGeneratedInsights] = useState(false);
  const [numGoals, setNumGoals] = useState(5); // Number of insights to generate, default 5

  // Scroll control
  const scrollContainerRef = React.useRef(null);
  const scrollAmount = 350; // adjust as needed based on card width

  // Define custom colors or use the colors passed as props
  const themeColors = colors || {
    navbarBg: '#0b4e5aff',
    fontColor: '#0b4e5aff',
    borderColor: '#178da1ff',
    buttonColor: '#083c46ff',
    iconColor: '#9acd32',  
    titleIconColor: '#9acd32',
    servicesBg: '#f6f3ec'
  };

  // Handle generating insights with direct API call
  const handleGenerateInsights = async () => {
    if (!currentSummary || !currentSummary.summary) return;
    
    setIsPending(true);
    setError(null);

    try {
      // Structure the request in the correct format using the values received from props
      const requestBody = {
        BearerToken: bearerToken,
        username: username,
        service: 1,
        request: {
          summary: currentSummary.summary,
          num_goals: numGoals  // Use the number of insights selected by the user
        }
      };
      // Direct API call using axios
      const response = await axios.post(`${react_api}/externalService/novaServices`, requestBody, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // Verify if the response contains the goals in the expected format
      if (response.data && response.data.request && response.data.request.goals) {
        setGoals(response.data.request.goals);
        // Reset selection
        setSelectedGoal(null);
        if (onGoalSelect) onGoalSelect(null);
        // Set state to indicate that insights have been generated at least once
        setHasGeneratedInsights(true);
      } else if (response.data && response.data.insights) {
        // Alternative format where insights are directly in the response
        setGoals(response.data.insights);
        setSelectedGoal(null);
        if (onGoalSelect) onGoalSelect(null);
        // Set state to indicate that insights have been generated at least once
        setHasGeneratedInsights(true);
      } else {
        setError('Invalid response from server');
        console.error('Unexpected response structure:', response.data);
      }
    } catch (err) {
      console.error('Error generating goals:', err);
      
      let errorMessage = 'Error generating insights';
      
      // Specific error handling for validation errors (422)
      if (err.response?.status === 422) {
        // Extract the detailed validation error message
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          // Take the first detailed error message
          errorMessage = errorDetail[0]?.msg || 'Validation error';
        }
      } else {
        // Generic error handling for other errors
        errorMessage = err.response?.data?.message || err.message || 'Error generating insights';
      }
      
      setError(errorMessage);
    } finally {
      setIsPending(false);
    }
  };

  // Handle goal selection
  const handleGoalSelect = (goal) => {
    setSelectedGoal(goal);
    if (onGoalSelect) onGoalSelect(goal);
  };

  // Handle scroll
  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      if (direction === 'left') {
        scrollContainerRef.current.scrollLeft -= scrollAmount;
      } else {
        scrollContainerRef.current.scrollLeft += scrollAmount;
      }
    }
  };

  // Extract visualization type from visualization string
  const getVisualizationType = (visualizationString) => {
    if (visualizationString.includes('scatter')) return 'Scatter Plot';
    if (visualizationString.includes('bar')) return 'Bar Chart';
    if (visualizationString.includes('box')) return 'Box Plot';
    if (visualizationString.includes('line')) return 'Line Chart';
    if (visualizationString.includes('pie')) return 'Pie Chart';
    if (visualizationString.includes('histogram')) return 'Histogram';
    return 'Chart';
  };

  // Customize the colors to match the Nova theme
  const chipBgColor = themeColors.servicesBg;
  const chipTextColor = themeColors.fontColor;
  const chipBorderColor = themeColors.borderColor;
  const scrollButtonBgColor = 'white';
  const scrollButtonHoverBgColor = themeColors.servicesBg;

  return (
    <Box mt={4} mb={4}>
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          bg={themeColors.buttonColor}
          color="white"
          onClick={handleGenerateInsights}
          isDisabled={isPending || !currentSummary}
          leftIcon={
            hasGeneratedInsights ? (
              <Box as="span" color={themeColors.titleIconColor}>↻</Box> 
            ) : (
              <Box as="span" color={themeColors.titleIconColor}>💡</Box>
            )
          }
          h="40px"
          borderRadius="md"
          fontWeight={600}
          _hover={{ bg: '#0a4956' }}
          boxShadow="sm"
        >
          {hasGeneratedInsights ? 'Refresh Insights' : 'Generate Insights'}
        </Button>
        
        <Flex alignItems="center" p={2} bg="white">
          <Text fontSize="sm" fontWeight="medium" color={themeColors.fontColor} mr={2}>Insights:</Text>
          <Flex>
            {[3, 5, 10].map((value) => (
              <Button 
                key={value}
                size="sm"
                variant={numGoals === value ? "solid" : "outline"}
                onClick={() => setNumGoals(value)}
                borderRadius="md"
                mx={1}
                bg={numGoals === value ? themeColors.buttonColor : "white"}
                color={numGoals === value ? "white" : themeColors.fontColor}
                borderColor={themeColors.borderColor}
                _hover={{ bg: numGoals === value ? themeColors.buttonColor : "gray.100" }}
              >
                {value}
              </Button>
            ))}
          </Flex>
        </Flex>
      </Flex>

      {/* Show skeleton during loading */}
      {isPending && (
        <Box mt={4}>
          <Flex gap={4} overflow="hidden" pb={2}>
            {[1, 2, 3].map((num) => (
              <Box 
                key={num}
                minWidth="300px"
                maxWidth="350px"
                height="350px"
                borderWidth="2px"
                borderRadius="md"
                borderColor={themeColors.borderColor}
                overflow="hidden"
                boxShadow="sm"
              >
                <Box 
                  p={4} 
                  bg={themeColors.navbarBg} 
                  height="64px"
                  borderBottomWidth="2px"
                  borderColor={themeColors.borderColor}
                >
                  <Flex align="center">
                    <Skeleton height="36px" width="36px" borderRadius="full" mr={4} />
                    <Box>
                      <Skeleton height="18px" width="120px" mb={2} />
                      <Skeleton height="16px" width="80px" />
                    </Box>
                  </Flex>
                </Box>
                <Box p={4}>
                  <Skeleton height="24px" width="90%" mb={4} />
                  <Skeleton height="24px" width="70%" mb={4} />
                  <Skeleton height="24px" width="80%" mb={8} />
                  <Skeleton height="1px" mb={4} />
                  <Skeleton height="18px" width="90%" />
                </Box>
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {error && (
        <Alert status="error" width="100%" mb={3} borderRadius="md" borderWidth="1px" borderColor={themeColors.borderColor}>
          <AlertIcon color="red.500" />
          <Text color={themeColors.fontColor}>{error}</Text>
        </Alert>
      )}

      {goals.length > 0 && !isPending && (
        <Box position="relative" px={6}>
          <Text fontSize="lg" fontWeight="medium" mb={2} color={themeColors.fontColor}>
            Select an insight to explore:
          </Text>

          <IconButton
            aria-label="Scroll left"
            icon={<Box as="span">←</Box>}
            position="absolute"
            top="50%"
            transform="translateY(-50%)"
            left={0}
            bg={scrollButtonBgColor}
            boxShadow="md"
            _hover={{ bg: scrollButtonHoverBgColor }}
            zIndex={1}
            onClick={() => handleScroll('left')}
          />

          <Box
            ref={scrollContainerRef}
            display="flex"
            overflowX="auto"
            gap={2}
            py={1}
            pb={2}
            px={1}
            sx={{
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(247, 247, 247, 0.7)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'gray.400',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'gray.500',
                },
              },
            }}
          >
            {goals.map((goal) => (
              <Card
                key={goal.index}
                minWidth="300px"
                maxWidth="350px"
                height="350px"
                display="flex"
                flexDirection="column"
                transition="transform 0.2s, box-shadow 0.2s"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'lg',
                }}
                cursor="pointer"
                borderRadius="md"
                overflow="hidden"
                border="2px solid"
                borderColor={selectedGoal?.index === goal.index ? themeColors.buttonColor : themeColors.borderColor}
                bg={selectedGoal?.index === goal.index ? '#e6f2f5' : 'white'}
                boxShadow="sm"
                onClick={() => handleGoalSelect(goal)}
              >
                <Flex
                  as={CardHeader}
                  alignItems="center"
                  borderBottom="2px solid"
                  borderColor={themeColors.borderColor}
                  bg={themeColors.navbarBg}
                  padding={4}
                  color="white"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    width="36px"
                    height="36px"
                    borderRadius="full"
                    marginRight={4}
                    color="white"
                    bg={themeColors.titleIconColor}
                    boxShadow="0 0 0 2px white"
                  >
                    {selectedGoal?.index === goal.index ? (
                      <Box as="span" fontWeight="bold">✓</Box>
                    ) : (
                      <Box as="span" fontWeight="bold">📊</Box>
                    )}
                  </Flex>
                  <Box flex={1}>
                    <Text fontSize="md" fontWeight={600}>
                      Insight {goal.index + 1}
                    </Text>
                    <Tag
                      size="sm"
                      fontSize="0.75rem"
                      fontWeight="medium"
                      borderRadius="full"
                      mt={1}
                      bg={chipBgColor}
                      color={chipTextColor}
                      border="1px solid"
                      borderColor={chipBorderColor}
                    >
                      {getVisualizationType(goal.visualization)}
                    </Tag>
                  </Box>
                </Flex>

                <CardBody
                  p={4}
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  overflow="hidden"
                >
                  <Text fontWeight={500} mb={2} noOfLines={3} overflow="hidden" color={themeColors.fontColor}>
                    {goal.question}
                  </Text>

                  <Box>
                    <Divider my={2} />

                    <Text
                      fontSize="0.85rem"
                      color="gray.500"
                      fontStyle="italic"
                      noOfLines={2}
                      overflow="hidden"
                    >
                      {goal.visualization}
                    </Text>
                  </Box>
                </CardBody>
              </Card>
            ))}
          </Box>

          <IconButton
            aria-label="Scroll right"
            icon={<Box as="span">→</Box>}
            position="absolute"
            top="50%"
            transform="translateY(-50%)"
            right={0}
            bg={scrollButtonBgColor}
            boxShadow="md"
            _hover={{ bg: scrollButtonHoverBgColor }}
            zIndex={1}
            onClick={() => handleScroll('right')}
          />
        </Box>
      )}
    </Box>
  );
};

export default GoalInsightsView;
